import { useEffect, useState } from 'react';
import { capturePostHogEvent, getPostHogClient } from '../utils/analytics';
import { isWebsiteThemeTrafficEligible } from './websiteTheme';
import { isPreviewExperimentOverrideHost } from '../utils/experimentOverrideHosts';

export const SIGN_IN_FLAG = 'signin-box-2026-09-13';
export type SignInVariant = 'control' | 'test';
const production = () => isWebsiteThemeTrafficEligible(window.location.hostname);
const storageKey = `botspot:${SIGN_IN_FLAG}`;
const valid = (value: unknown): value is SignInVariant => value === 'control' || value === 'test';
export function signInPreview(): SignInVariant | null {
  if (!isPreviewExperimentOverrideHost()) return null;
  const value = new URLSearchParams(window.location.search).get('signInDesign');
  return value === 'orange' ? 'control' : value === 'clarity' ? 'test' : null;
}
export default function useSignInExperiment(enabled: boolean) {
  const [result, setResult] = useState<{
    variant: SignInVariant;
    enrolled: boolean;
    ready: boolean;
  }>({
    variant: signInPreview() || 'test',
    enrolled: false,
    ready: !enabled || !production(),
  });
  useEffect(() => {
    if (!enabled) return;
    const preview = signInPreview();
    if (preview) {
      setResult({ variant: preview, enrolled: false, ready: true });
      return;
    }
    if (!production()) return;
    const client = getPostHogClient({ initialize: true });
    if (!client) {
      setResult({ variant: 'test', enrolled: false, ready: true });
      return;
    }
    let settled = false;
    setResult({ variant: 'test', enrolled: false, ready: false });
    const finish = (variant: SignInVariant, enrolled: boolean) => {
      if (settled) return;
      settled = true;
      setResult({ variant, enrolled, ready: true });
    };
    const evaluate = () => {
      if (settled) return;
      let value;
      try {
        value = client.getFeatureFlag(SIGN_IN_FLAG, { send_event: false });
      } catch {
        finish('test', false);
        return;
      }
      if (value === false) return finish('test', false);
      if (!valid(value)) return;
      let variant = value;
      try {
        const saved = localStorage.getItem(storageKey);
        if (valid(saved)) variant = saved;
        else localStorage.setItem(storageKey, variant);
      } catch {
        /* The current dialog still keeps a stable assignment. */
      }
      finish(variant, true);
    };
    evaluate();
    let unsubscribe: (() => void) | void;
    try {
      unsubscribe = client.onFeatureFlags(evaluate);
    } catch {
      finish('test', false);
    }
    const timeout = window.setTimeout(() => finish('test', false), 3000);
    return () => {
      settled = true;
      unsubscribe?.();
      window.clearTimeout(timeout);
    };
  }, [enabled]);
  useEffect(() => {
    if (!enabled || !result.ready || !result.enrolled) return;
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => {
        capturePostHogEvent('$feature_flag_called', {
          $feature_flag: SIGN_IN_FLAG,
          $feature_flag_response: result.variant,
          experiment_name: SIGN_IN_FLAG,
          feature_flag: SIGN_IN_FLAG,
          variant: result.variant,
          [`$feature/${SIGN_IN_FLAG}`]: result.variant,
          sign_in_design: result.variant === 'control' ? 'orange' : 'clarity',
        });
      });
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [enabled, result]);
  return {
    ...result,
    ready: !enabled || result.ready,
    design: (enabled && result.variant === 'control' ? 'orange' : 'clarity') as
      | 'orange'
      | 'clarity',
    analytics:
      enabled && result.enrolled
        ? {
            sign_in_experiment: SIGN_IN_FLAG,
            sign_in_variant: result.variant,
            [`$feature/${SIGN_IN_FLAG}`]: result.variant,
          }
        : {},
  };
}
